import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { globalMutate } from "@utils/swr";
import { createCollar } from "@services/device/collar/create";
import { ComboBoxDefault } from "@typings/components/comboBox";
import ComboBox from "@commons/Input/ComboBox";
import { searchIndividuals } from "@services/individuals/search";
import type { Individual } from "@typings/entities/individuals";

const collarSchema = z.object({
  number: z.string().min(1, "Número do dispositivo é obrigatório"),
  individual: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullable()
    .optional(),
  responsibleTeam: z.string().optional(),
});

type CollarFormData = z.infer<typeof collarSchema>;

function ModalNewCollar() {
  const { isModalOpen, closeModal, setIsLoading } = usePage();
  const open = isModalOpen("newCollar");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CollarFormData>({
    resolver: zodResolver(collarSchema),
    defaultValues: {
      number: "",
      responsibleTeam: "",
      individual: ComboBoxDefault,
    },
  });

  const onSubmit = async (data: CollarFormData) => {
    try {
      setIsLoading(true);

      await createCollar({
        number: data.number,
        responsibleTeam: data.responsibleTeam || undefined,
        individualId: data.individual?.value || undefined,
      });

      toast.success("Rastreador criado com sucesso");
      await globalMutate(["fetch-get-collars", "fetch-collars-geojson"]);
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao criar Rastreador";

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Cadastrar um Rastreador"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-2xl relative"
    >
      <form className="grid grid-cols-1 text-left gap-4 sm:gap-6">
        <Controller
          name="number"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Número do dispositivo"
              textSize="sm"
              fullWidth
              error={!!errors.number}
              helperText={errors.number?.message}
            />
          )}
        />

        <Controller
          name="individual"
          control={control}
          render={({ field }) => (
            <div>
              <ComboBox<Individual>
                {...field}
                label="Indivíduo"
                value={field.value ?? null}
                onChange={(option) => field.onChange(option ?? null)}
                // Filter for individuals without GPS to prevent double-assignment of collars
                searchService={(query) =>
                  searchIndividuals(query, { hasGPS: "false" })
                }
                error={!!errors.individual}
                helperText={errors.individual?.label?.message}
              />
            </div>
          )}
        />

        <Controller
          name="responsibleTeam"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Equipe Responsável"
              type="text"
              textSize="sm"
              fullWidth
              error={!!errors.responsibleTeam}
              helperText={errors.responsibleTeam?.message}
            />
          )}
        />
      </form>
    </Modal>
  );
}

export default ModalNewCollar;
